/* eslint-disable react/prop-types */
import { ApiClient } from '@api/api-configuration';
import { CaregiverMinified } from '@api/READ_ONLY/caregiver_api/Api';
import { captureError } from '@app/lib/Sentry';
import CaregiversSelectGeneric from '@feat-caregivers/components/CaregiversSelect';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomComponentInputs } from '../filters.types';

/**
 * Caregiver select wrapper for query filter system
 * Handles both single and multiple selection modes
 * Fetches caregiver objects when IDs are provided from URL
 */
export const CaregiverFilterSelect: React.FC<CustomComponentInputs> = ({ onChange, value }) => {
  const { t } = useTranslation();
  const [loadedCaregivers, setLoadedCaregivers] = useState<CaregiverMinified[]>([]);

  // Normalize value to always be an array for mode="multiple"
  const normalizedValue = useMemo(() => {
    if (!value) return undefined;
    if (Array.isArray(value)) {
      return value.map((v) => Number(v));
    }
    return [Number(value)];
  }, [value]);

  // Fetch caregivers by IDs when value is provided (e.g., from URL)
  useEffect(() => {
    const fetchCaregiversByIds = async (ids: number[]) => {
      try {
        const caregivers = await Promise.all(
          ids.map(async (id) => {
            try {
              const response = await ApiClient.caregiverApi.caregivers.getCaregiverById(id);
              return response.data as CaregiverMinified;
            } catch (error) {
              captureError(error instanceof Error ? error : new Error(`Error fetching caregiver ID ${id}`), {
                caregiverId: id,
                component: 'CaregiverFilterSelect',
              });
              return null;
            }
          })
        );
        const filtered = caregivers.filter((c) => c !== null) as CaregiverMinified[];
        setLoadedCaregivers(filtered);
      } catch (error) {
        captureError(error instanceof Error ? error : new Error('Error fetching caregivers'), {
          caregiverIds: ids,
          component: 'CaregiverFilterSelect',
        });
      }
    };

    if (normalizedValue && normalizedValue.length > 0) {
      fetchCaregiversByIds(normalizedValue);
    } else {
      setLoadedCaregivers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedValue]);

  const handleChange = (caregiver: CaregiverMinified | CaregiverMinified[] | undefined) => {
    if (Array.isArray(caregiver)) {
      // Multiple selection - return array of IDs
      onChange(caregiver.map((c) => c.caregiverId));
    } else if (caregiver) {
      // Single selection - return single ID
      onChange(caregiver.caregiverId);
    } else {
      // Cleared
      onChange(null);
    }
  };

  return (
    <CaregiversSelectGeneric
      allowClear={true}
      mode="multiple"
      placeholder={t('filters-select-caregivers-placeholder')}
      maxTagCount="responsive"
      onCaregiverChange={handleChange}
      value={normalizedValue}
      selectedItemsProvider={() => (loadedCaregivers.length > 0 ? loadedCaregivers : undefined)}
      style={{ width: '100%', height: '44px' }}
      className="h-8"
    />
  );
};
